/** @jsx jsx */
import React, { ReactChildren, useCallback, useEffect, useRef, useState } from 'react';
import { css, jsx } from '@emotion/react';
import { ReactJSXElement } from '@emotion/react/types/jsx-namespace';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { logCuratedViewVisit, updateCuratedViewFavStatus } from 'widgets/endpoints';
import { useParams } from 'react-router-dom';
import { IsPageLoaded } from 'views/curatedViews/LoadingContext';

/**
 * Each curated view should live inside this. This takes care of the scrolling and general layout.
 * Usage example: See /serberus/cyhy/src/curatedViews/singleOrg/index
 * @param props
 * @param props.loaded - A flag telling the container when the children have completed loading. Used to know when to do
 * 	some measurements and set up page.
 * @param props.children - the main section of the page. What contents should be shown in this curated view
 * @param props.topSection - the top section, above the main contents, but below the page nav bar. This is the part that should shrink when scrolled
 * @param props.publicUrl
 * @constructor
 */
interface CuratedViewContainerProps {
	children: ReactChildren;
	topSection: (arg1: boolean, arg2: () => void, arg3: React.MutableRefObject<undefined>) => ReactJSXElement;
	publicURL: string;
	metaData: {
		key: string;
		name: string;
		fullName: string;
		group: string;
	}
	setTopSectionHeight: (arg1: number) => void;
}
export function CuratedViewContainer(props: CuratedViewContainerProps) {
	const rootRef = useRef<HTMLDivElement>();
	const [shrink, setShrink] = useState(false);
	const [scrollBarWidth, setScrollBarWidth] = useState(0);

	let { id, group } = useParams<{id, group}>();
	id = decodeURIComponent(id);

	const [isFavorite, setFavorite] = useState(false);
	const toggleFavorite = useCallback(() => {
		setFavorite(s => {
			const newState = !s;
			updateCuratedViewFavStatus({id, favorite: newState});
			return newState;
		});
	},[]);

	const pageLoaded = IsPageLoaded();

	//Even though this effect and the one below it both have a dep on props.loaded it was more work to make them live in the
	//same useAsyncCallback. So just split them up.
	useAsyncCallback(async () => {
		if(pageLoaded) {
			const {metaData: {name}} = props;
			if (!id || !name || !group) {
				throw new Error(`Don't have all the pieces to log this visit!! Id: ${id}, Name: ${name}, Group: ${group}`);
			}
			const { favorite } = await logCuratedViewVisit({ id, name, type: group });
			setFavorite(favorite);
		}
	}, [pageLoaded]);

	useEffect(() => {
		if(pageLoaded) {
			const el = rootRef?.current;
			setScrollBarWidth(el.offsetWidth - el.clientWidth);

			//Set the height to auto, so that it gets as big as it needs to (overwrite any previously set css height settings)
			//(Note that if the height was set, then the top section changed height we need this otherwise the first set height
			// always is returned)
			//@ts-ignore
			topSectionRef?.current?.style.height = "auto";
			// @ts-ignore  Get the height of the top section
			props.setTopSectionHeight(topSectionRef?.current?.clientHeight || 1);
			//@ts-ignore  Take away the height:auto style, and let the css take back over
			topSectionRef?.current?.style.removeProperty("height");
		}
	}, [pageLoaded]);

	const _onScroll = (e) => {
		if (e.target.scrollTop > 50) {
			setShrink(true);
		} else {
			setShrink(false);
		}
	};

	const topSectionRef = useRef();
	return (
		<div css={cardsSection(pageLoaded)} onScroll={_onScroll} className={`${shrink ? 'shrink' : ''} flex-column`} ref={rootRef}>
			<div className="overflow-container">
				<div css={curatedViewCss(props, scrollBarWidth)} className={`flex-column`}>
					{props.topSection(isFavorite, toggleFavorite, topSectionRef)}
				</div>
				{props.children}
			</div>
		</div>
	);
}


const cardsSection = loaded => css`
	overflow: ${loaded ? "auto" : "hidden"};
	
	& .overflow-container { //the container that "overflows" allowing scrolling at the cardsSection div level
		min-width: 1150px;
		overflow: hidden;
	}
	
	& .bold-me {
		font-weight: bold;
	}

	& .flex-1 {
		flex: 1;
	}

	& .flex-row {
		display: flex;
		flex-direction: row;
	}
	& .flex-column {
		display: flex;
		flex-direction: column;
	}

	& .section-title {
		margin: 18px 50px 10px 50px;
		font-weight: bold;
		font-size: 1.2em;
		color: #6699CC;
	}
	
	
`;

const curatedViewCss = ({ publicURL }, scrollBarWidth) => css`
	color: white;
	cursor: default;
	width: 100%;
	content: ' ';
	position: absolute;
	right: ${scrollBarWidth}px;

	overflow: hidden;

`;


export function Section({ children }) {
	return <div css={sectionCss}>{children}</div>;
}

const sectionCss = css`
	margin: 10px 50px;
	display: grid;
	grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
	column-gap: 10px;
	row-gap: 10px;
`;
